<template>
  <!-- =========================================================
       FLOATING HELP CENTER LAUNCHER
  ========================================================== -->
  <button
    type="button"
    class="ai-help-launcher"
    :class="{ 'ai-help-launcher--expanded': hovered }"
    :aria-label="
      isFrench
        ? 'Ouvrir le centre d’aide'
        : 'Open Help Center'
    "
    @mouseenter="expand"
    @mouseleave="collapse"
    @click="openHelpCenter"
  >
    <!-- Text -->
    <span class="ai-help-launcher__label">
      {{
        isFrench
          ? "Demandez à Nexgbits"
          : "Ask Nexgbits"
      }}
    </span>

    <!-- Icon -->
    <span class="ai-help-launcher__icon">
      <v-icon size="28">
        mdi-creation
      </v-icon>
    </span>
  </button>


  <!-- =========================================================
       EXISTING SMART HELP CENTER
  ========================================================== -->
  <ChatbotDialognew
  :visible="helpCenterVisible"
  @close="closeHelpCenter"
/>
</template>


<script setup>
import {
  computed,
  ref
} from "vue";

import {
  useI18n
} from "vue-i18n";

import ChatbotDialognew from "@/components/ChatbotDialognew.vue"


// =============================================================
// LANGUAGE
// =============================================================

const { locale } = useI18n();


const isFrench = computed(() => {

  return String(
    locale.value || "en"
  )
    .toLowerCase()
    .startsWith("fr");

});


// =============================================================
// STATE
// =============================================================

const hovered = ref(false);

const helpCenterVisible = ref(false);


// =============================================================
// LAUNCHER
// =============================================================

function expand() {

  hovered.value = true;

}


function collapse() {

  hovered.value = false;

}


function openHelpCenter() {

  /*
   * Collapse launcher immediately.
   */
  hovered.value = false;


  /*
   * Open Smart Help Center.
   */
  helpCenterVisible.value = true;

}


function closeHelpCenter() {

  helpCenterVisible.value = false;

}
</script>


<style scoped>
/* ============================================================
   FLOATING HELP CENTER LAUNCHER
============================================================ */

.ai-help-launcher {
  position: fixed !important;

  right: 24px !important;
  bottom: 24px !important;

  /*
   * We know from your red test that this level works.
   */
  z-index: 2147483647 !important;

  width: 60px;
  min-width: 60px;
  height: 60px;

  margin: 0;
  padding: 0;

  display: flex;
  align-items: center;
  justify-content: flex-end;

  overflow: hidden;

  border:
    2px solid
    rgba(255, 255, 255, 0.95);

  border-radius: 30px;

  background:
    linear-gradient(
      135deg,
      #000033 0%,
      #10275e 52%,
      #214a96 100%
    );

  color: #ffffff;

  box-shadow:
    0 8px 25px
      rgba(0, 0, 51, 0.30),
    0 3px 9px
      rgba(0, 0, 0, 0.16);

  cursor: pointer;

  white-space: nowrap;

  /*
   * The right position stays fixed.
   * Increasing width therefore expands LEFT.
   */
  transform-origin: right center;

  transition:
    width 0.30s
      cubic-bezier(
        0.4,
        0,
        0.2,
        1
      ),
    box-shadow 0.25s ease;
}


/* ============================================================
   EXPANDED
============================================================ */

.ai-help-launcher--expanded {
  width: 215px;
}


/* ============================================================
   LABEL
============================================================ */

.ai-help-launcher__label {
  display: block;

  width: 0;
  max-width: 0;

  padding: 0;

  overflow: hidden;

  opacity: 0;

  color: #ffffff;

  font-size: 14px;
  font-weight: 600;

  line-height: 1;

  letter-spacing: 0.01em;

  text-align: left;

  white-space: nowrap;

  transform:
    translateX(12px);

  transition:
    width 0.30s ease,
    max-width 0.30s ease,
    padding 0.30s ease,
    opacity 0.20s ease,
    transform 0.30s ease;
}


.ai-help-launcher--expanded
.ai-help-launcher__label {
  width: 153px;
  max-width: 153px;

  padding-left: 18px;

  opacity: 1;

  transform:
    translateX(0);
}


/* ============================================================
   ICON
============================================================ */

.ai-help-launcher__icon {
  width: 58px;
  min-width: 58px;
  height: 58px;

  flex:
    0 0
    58px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;

  transition:
    transform 0.28s ease;
}


/*
 * Gentle rotation that you liked.
 */
.ai-help-launcher:hover
.ai-help-launcher__icon {
  transform:
    rotate(8deg);
}


/* ============================================================
   HOVER SHADOW
============================================================ */

.ai-help-launcher:hover {
  box-shadow:
    0 12px 34px
      rgba(0, 0, 51, 0.38),
    0 4px 12px
      rgba(0, 0, 0, 0.18);
}


/* ============================================================
   KEYBOARD ACCESSIBILITY
============================================================ */

.ai-help-launcher:focus-visible {
  outline:
    3px solid
    rgba(64, 145, 255, 0.50);

  outline-offset: 4px;
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 768px) {

  .ai-help-launcher {
    right: 16px !important;
    bottom: 16px !important;

    width: 56px;
    min-width: 56px;
    height: 56px;

    border-radius: 28px;
  }


  /*
   * Touch devices stay compact.
   */
  .ai-help-launcher--expanded {
    width: 56px;
  }


  .ai-help-launcher__label,
  .ai-help-launcher--expanded
  .ai-help-launcher__label {
    display: none;
  }


  .ai-help-launcher__icon {
    width: 54px;
    min-width: 54px;
    height: 54px;

    flex-basis: 54px;
  }
}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (
  prefers-reduced-motion: reduce
) {

  .ai-help-launcher,
  .ai-help-launcher__label,
  .ai-help-launcher__icon {
    transition: none;
  }

}
</style>